import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getPermits } from "../../api/permit";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "../../utils/date";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";
import MobileExcelUpload from "../../components/permit/MobileExcelUpload";
import { FileText, Calendar, Building2, AlertCircle } from "lucide-react";

const STATUS_LABELS = {
  planned: "계획",
  in_progress: "진행중",
  completed: "완료",
  delayed: "지연",
};
const STATUS_STYLES = {
  planned: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  delayed: "bg-red-100 text-red-700",
};

const ALL_STATUS = Object.keys(STATUS_LABELS);

export default function PermitDashboardPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;
  const [loading, setLoading] = useState(true);

  const [statuses, setStatuses] = useState(ALL_STATUS);
  const [ordering, setOrdering] = useState("-id");
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    getPermits()
      .then((data) => {
        const list = data.results || data;
        setRows(list);
        setFilteredRows(list);
        setTotal(list.length);
      })
      .catch((err) => {
        console.error("인허가 목록 불러오기 실패:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let result = [...rows];

    if (statuses.length > 0) {
      result = result.filter((r) => statuses.includes(r.status));
    }

    if (q.trim() !== "") {
      const keyword = q.trim().toLowerCase();
      result = result.filter(
        (r) =>
          r.name?.toLowerCase().includes(keyword) ||
          r.permit_type?.toLowerCase().includes(keyword) ||
          r.authority?.toLowerCase().includes(keyword)
      );
    }

    if (ordering === "id") result.sort((a, b) => a.id - b.id);
    else if (ordering === "-id") result.sort((a, b) => b.id - a.id);
    else if (ordering === "planned_date")
      result.sort((a, b) =>
        (a.planned_date || "").localeCompare(b.planned_date || "")
      );
    else if (ordering === "-planned_date")
      result.sort((a, b) =>
        (b.planned_date || "").localeCompare(a.planned_date || "")
      );

    setFilteredRows(result);
    setTotal(result.length);
    setPage(1);
  }, [statuses, q, ordering, rows]);

  const pagedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  const toggleStatus = (s) => {
    setStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const applySearch = () => {
    setPage(1);
  };

  const statusCount = {
    planned: filteredRows.filter((p) => p.status === "planned").length,
    in_progress: filteredRows.filter((p) => p.status === "in_progress").length,
    completed: filteredRows.filter((p) => p.status === "completed").length,
    delayed: filteredRows.filter((p) => p.status === "delayed").length,
  };

  const SkeletonCard = () => (
    <div className="bg-white rounded-lg p-3 border animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2 mb-1"></div>
      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
    </div>
  );

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-2 border min-w-0">
          <h2 className="text-base font-bold text-gray-900">인허가 현황</h2>
          <p className="text-[10px] text-gray-500 mt-0.5">전체 {total}건</p>
        </div>

        {/* 현황 요약 */}
        <div className="bg-white rounded-lg p-2 border overflow-hidden min-w-0">
          <div className="text-[10px] font-medium text-gray-500 mb-1.5">
            현황 요약
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div className="bg-gray-50 rounded-lg px-2 py-1.5 border overflow-hidden">
              <div className="text-[10px] text-gray-500 truncate">전체</div>
              <div className="text-base font-bold text-gray-900 truncate">
                {total}
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg px-2 py-1.5 border border-blue-200 overflow-hidden">
              <div className="text-[10px] text-blue-600 truncate">진행중</div>
              <div className="text-base font-bold text-blue-700 truncate">
                {statusCount.in_progress}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg px-2 py-1.5 border border-green-200 overflow-hidden">
              <div className="text-[10px] text-green-600 truncate">완료</div>
              <div className="text-base font-bold text-green-700 truncate">
                {statusCount.completed}
              </div>
            </div>
            <div className="bg-red-50 rounded-lg px-2 py-1.5 border border-red-200 overflow-hidden">
              <div className="text-[10px] text-red-600 truncate">지연</div>
              <div className="text-base font-bold text-red-700 truncate">
                {statusCount.delayed}
              </div>
            </div>
          </div>
        </div>

        {/* 필터 & 검색 */}
        <div className="bg-white rounded-lg p-2 border space-y-2 min-w-0">
          {/* 상태 필터 */}
          <div className="space-y-1">
            <div className="text-[10px] font-medium text-gray-500">
              상태 필터
            </div>
            <div className="grid grid-cols-2 gap-1">
              {ALL_STATUS.map((s) => {
                const active = statuses.includes(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleStatus(s)}
                    className={`px - 2 py - 1 rounded text - [10px] font - medium border transition - all ${active
                      ? "border-blue-400 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-600 hover:bg-gray-200 hover:border-gray-400"
                      } `}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                );
              })}
              <button
                onClick={() => setStatuses(ALL_STATUS)}
                className="btn-basic-sm col-span-2 text-[10px] px-2 py-1"
              >
                전체
              </button>
            </div>
          </div>

          {/* 정렬 */}
          <div className="space-y-1">
            <div className="text-[10px] font-medium text-gray-500">정렬</div>
            <select
              className="w-full bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={ordering}
              onChange={(e) => setOrdering(e.target.value)}
            >
              <option value="-id">ID 내림차</option>
              <option value="id">ID 오름차</option>
              <option value="-planned_date">예정일 최신순</option>
              <option value="planned_date">예정일 오래된순</option>
            </select>
          </div>

          {/* 검색 */}
          <div className="space-y-1">
            <div className="text-[10px] font-medium text-gray-500">검색</div>
            <div className="flex items-center gap-1.5">
              <input
                className="flex-1 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="검색어 입력..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySearch()}
              />
              <button
                className="btn-search flex-shrink-0 whitespace-nowrap text-xs px-2 py-1.5"
                onClick={applySearch}
              >
                검색
              </button>
            </div>
          </div>
        </div>

        {/* Excel Upload */}
        <div className="bg-white rounded-lg p-2 border overflow-hidden min-w-0">
          <div className="max-w-full overflow-hidden min-w-0">
            <MobileExcelUpload onUploaded={() => window.location.reload()} />
          </div>
        </div>

        {/* 인허가 리스트 */}
        <div className="space-y-2 min-w-0">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : pagedRows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">등록된 인허가가 없습니다.</p>
              <button
                onClick={() => navigate(`/project/${id}/permit/new`)}
                className="btn-create mt-4"
              >
                새 인허가 등록
              </button>
            </div>
          ) : (
            pagedRows.map((row) => (
              <button
                key={row.id}
                onClick={() =>
                  navigate(`/project/${id}/permit/view/${row.doc_dns}`)
                }
                className="w-full text-left bg-white rounded-lg p-3 border hover:border-blue-300 active:bg-gray-50 transition-all overflow-hidden"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-gray-900 truncate flex-1 min-w-0">
                        {row.name || "제목 없음"}
                      </h3>
                      <span
                        className={`flex - shrink - 0 px - 2 py - 0.5 rounded text - xs font - medium whitespace - nowrap ${STATUS_STYLES[row.status]
                          } `}
                      >
                        {STATUS_LABELS[row.status]}
                      </span>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 truncate flex-1 min-w-0">
                          {row.authority || "-"}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 truncate flex-1 min-w-0">
                          {row.permit_type || "-"}
                        </span>
                      </div>

                      {row.planned_date && (
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-600 truncate flex-1 min-w-0">
                            예정: {formatDate(row.planned_date)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {row.status === "delayed" && (
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="bg-white rounded-lg p-3 border min-w-0">
          <MobileBoardPagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={(p) => setPage(p)}
            btnClassName="px-3 py-2 text-xs whitespace-nowrap"
          />
        </div>
      </div>
    </div>
  );
}
